import { AreaConfig } from "src/app/360-evaluation/_interfaces/area-config.interface";
import { Goal } from "src/app/goals/_interfaces/goal.interface";
import { AttributeItem } from "src/app/users/_interfaces/users-admin";
import { GoalTypeEnum } from "../enums/GoalTypeEnum";
import { GlobalizationConfig } from "./globalization-config.interface";
import { ProcessTypeEnum } from "./process.interface";
import { CalibrationType } from "src/app/calibration/_interfaces/ratings-data.interface";

export interface ProcessConfig {
    id: number;
    createdAt: Date;
    deletedAt?: Date;
    globalizationConfig: GlobalizationConfig;
    sendLimit: SendLimit;
    managerOnly: boolean;
    optionalExternalEval?: boolean;
    disableRelationColumn?: boolean;
    maximumGoalQuantity?: number;
    minimumGoalQuantity?: number;
    maximumEvaluationQuantity: number;
    type: ProcessTypeEnum;
    versionDate: Date;
    versionName: string;
    title: QuestionConfig;
    description: QuestionConfig;
    deadline: QuestionConfig;
    initialDeadline: QuestionConfig;
    weight: QuestionConfig;
    questionsGroups?: Array<QuestionGroup>;
    managerQuestionGroups?: Array<QuestionGroup>;
    questions?: Array<QuestionConfig>;
    twoPlusTwoCoachingIntegration: boolean;
    skipSelectionStep?: boolean;
    closed: boolean;
    startDate?: string;
    endDate?: string;
    areas?: AreaConfig[];
    draftStartDate?: Date;
    evaluationValuesTarget?: string;
    consentGoalsGlobalizationConfig: GlobalizationConfig;
    hideOnFinalReport?: boolean;
    consentGoals: Goal[];
    consentAttributes: AttributeItem[];
    goalType: GoalTypeEnum;
    skipTwoPlusTwoCoachingEvaluation: boolean;
    enableValuationLimit: boolean;
    threeSixtyEvaluationLimit: number;
    threeSixtyEvaluationTimespanInHours: number;
}

export interface ProcessCalibrationConfig {
    id?: number;
    versionName: string;
    Type: ProcessTypeEnum;
    globalizationConfig: GlobalizationConfig;
    calibrationType: CalibrationType;
}
export interface QuestionGroup {
    id?: number;
    guid?: string;
    category?: QuestionGroupCategory;
    order: number;
    showOnCalibration?: boolean;
    processId?: number;
    globalizationConfig: GlobalizationConfig;
    questions: Array<QuestionConfig>;
    attributes?: QuestionGroupAttribute[];
    onlyCalculateQuestions: boolean;
    hasCalculateQuestions?: boolean;
}

export interface QuestionGroupAttribute {
    questionGroupId: number;
    questionGroup?: QuestionGroup;
    attributeId: number;
    attribute?: AttributeItem;
}

export enum AndOrEnum {
    and = 0,
    or = 1,
}

export interface QuestionAttribute {
    questionId: number;
    question?: QuestionGroup;
    attributeId: number;
    attribute?: AttributeItem;
    anD_OR?: number;
}

export enum QuestionGroupCategory {
    Manager = 1,
}

export interface QuestionConfig {
    id?: number;
    guid?: string;
    order: number;
    required: boolean;
    showPreview: boolean;
    processId?: number;
    groupId?: number;
    groupGuid?: string;
    questionTypeConfigId?: number;
    type?: QuestionConfigTypeEnum;
    typeConfig: QuestionTypeConfig;
    globalizationConfig: GlobalizationConfig;
    attributesQuestionConfig?: Array<AttributeQuestionConfigs>;
    values?: Array<{ value: string; total: number }>;
    reportColumn?: string;
    expanded?: boolean;
    approved?: boolean;
    currentIndex?: number;
    checked?: boolean;
    groupedReportColumns?: string[];
    groupedGlobalizationConfig?: Array<GlobalizationConfig>;
    groupedTypes?: Array<QuestionConfigTypeEnum>;
    groupedIndexes?: Array<number>;
    groupedValues?: Array<Array<{ value: string; total: number }>>;
    hasQuestionAttributes?: boolean;
    goal?: Goal;
    groupedGoals?: Array<Goal>;
    showRequired?: boolean;
    attributes?: QuestionAttribute[];
    relatedQuestions: RelatedQuestion[];
    formula: QuestionFormulaType;
    viewType?: QuestionViewType;
    groupedViewType?: Array<QuestionViewType>;
    hideQuestion?: boolean;
}

export interface AttributeQuestionConfigs
{
    id?: number;
    attributeId: number;
    questionId: number;
    attribute?: AttributeItem;
    globalizationConfig: GlobalizationConfig;
}

export class RelatedQuestion {
    public parentQuestionId: number;
    public questionId: number;
    public question: QuestionConfig;
}

export enum QuestionFormulaType {
    Sum = 0,
    Average = 1,
}

export enum QuestionViewType {
    Left = 0,
    Top = 1,
    Right = 2,
    Bottom = 3,
}

export enum QuestionConfigTypeEnum {
    Goals,
    GoalsFinalGrade,
    FinalGrade,
    PrinciplesFinalGrade,
    LeadershipFinalGrade,
    Leadership,
    Comments,
    Potential,
    QualitativeAnalisys,
    FinalConceptOfLeadership,
}

export interface QuestionTypeConfig {
    id?: number;
    name: string;
    type: QuestionTypeEnum;
    max?: number;
    possibleValuesGlobalized?: ValuesGlobalization;
    possibleValues?: Array<any>;
    isConsentPossibleValues?: Array<boolean>;
    description?: ValuesGlobalization;
    confirmationTextEnabled?: Array<boolean>;
    confirmationText?: ValuesGlobalization;
    colors?: Array<string>;
    valueLimitEnabled?: Array<boolean>;
    valueLimit?: Array<number>;
    valuesAmount?: number;
    multipleSelected?: Array<string>;
    showQuestion?: boolean;
    showCalculateLabel?: boolean;
    showQuestionName?: boolean;
}

export enum QuestionTypeEnum {
    Text = 1,
    Boolean = 2,
    Slider = 3,
    GroupedSlider = 33,
    Selection = 4,
    Date = 5,
    Number = 6,
    MultiSelection = 7,
    Weight = 8,
    Description = 9,
    Title = 10,
    Deadline = 11,
    Calculate = 12,
    InitialDeadline = 13,
}

export interface SendLimit {
    QuestionId: number;
    DynamicLimitConcepts: boolean;
    Limits: Limits[];
}

export interface Limits {
    value: string;
    max?: number;
    actual?: number;
}

export class LimitConcepts {
    static Last: string = "LAST";
    static Penultimate: string = "PNLT";
    static Remaining: string = "RMNG";
}

export class BoolLimits {
    last: boolean = false;
    penultimate: boolean = false;
    remaining: boolean = false;
}

export class NumberLimits {
    last: number = 0;
    penultimate: number = 0;
    remaining: number = 0;
}

export interface ValuesGlobalization {
    "en-US": Array<string>;
    "es-ES": Array<string>;
    "pt-BR": Array<string>;
}

/**
 * Used in 360-evaluation, Feedback when the user has passed the deadline for removing excess users and the system automatically removes them
 */
export interface ProcessExpirationResult {
    isAutomaticRemovalPerformed: boolean;
    refusedUsers: number;
    stepExpirationDate?: Date;
}
